// Init Sentry — pensé mobile & offline-first.
//
// Offline-first : le transport `makeBrowserOfflineTransport` met les envelopes
// échouées (hors-ligne / 5xx) en file dans IndexedDB et les rejoue dès le
// retour réseau. Une erreur survenue dans un sous-sol ne se perd pas — même
// contrat que la queue de mutations métier.
//
// Désactivé proprement (no-op) si `VITE_SENTRY_DSN` est absent : la CI, les
// tests et les builds locaux sans DSN ne polluent pas le projet Sentry.
import * as Sentry from "@sentry/react";

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

// Transactions du cycle de sync : tracées à 100% (focus observabilité sync).
const SYNC_TX = new Set(["sync.cycle", "sync.flush", "sync.upload", "sync.refresh"]);

let started = false;

export function initSentry(): void {
  if (started || !DSN) return; // idempotent (StrictMode) + no-op sans DSN
  started = true;
  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.MODE, // "development" en dev, "production" au build
    release: (import.meta.env.VITE_RELEASE as string | undefined) ?? "dev",
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
    integrations: [
      Sentry.browserTracingIntegration(),
      // Données client (nom, adresse, tél) : on masque tout le texte et tous
      // les médias. Zéro PII ne quitte l'appareil dans le replay.
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampler: (ctx) => (SYNC_TX.has(ctx.name) ? 1.0 : 0.2),
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    initialScope: { tags: { app: "relais-front" } },
  });
}

export function setSentryUser(id: string): void {
  if (!DSN) return;
  Sentry.setUser({ id });
}

export function clearSentryUser(): void {
  if (!DSN) return;
  Sentry.setUser(null);
}

export function setConnectionStateTag(state: string): void {
  if (!DSN) return;
  Sentry.setTag("connection_state", state);
  Sentry.addBreadcrumb({
    category: "sync.connection",
    level: "info",
    message: state,
  });
}
